import React, { useEffect, useRef } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLocalizedLink } from "../../hooks/useLocalizedLink";

const CategoryOne = () => {
  const { t } = useTranslation();
  const getLocalizedPath = useLocalizedLink();
  const swiperRef = useRef(null);

  const categories = [
    {
      id: 1,
      key: "tripleRoom",
      imgSrc: "/assets/img/2-3-kisilik-yatay/2-3-kisilik-yatay (3).jpeg",
      link: "/service/triple-room",
    },
    {
      id: 2,
      key: "diningHall",
      imgSrc: "/assets/img/yurt-fotograf/yemekhane/5.jpg",
      link: "/service/social-areas",
    },
    {
      id: 3,
      key: "terrace",
      imgSrc: "/assets/img/yurt-fotograf/teras/7.jpg",
      link: "/service/social-areas",
    },
    {
      id: 4,
      key: "studyRoom",
      imgSrc: "/assets/img/yurt-fotograf/etut/1.jpg",
      link: "/service/social-areas",
    },
    {
      id: 5,
      key: "bathroom",
      imgSrc: "/assets/img/yurt-fotograf/banyo/24.jpg",
      link: "/gallery",
    },
    {
      id: 6,
      key: "singleRoom",
      imgSrc: "/assets/img/2-3-kisilik/2-3-kisilik-dikey (1).jpeg",
      link: "/service/single-room",
    },
    {
      id: 7,
      key: "doubleRoom",
      imgSrc: "/assets/img/2-3-kisilik-yatay/2-3-kisilik-yatay (1).jpeg",
      link: "/service/double-room",
    },
    {
      id: 8,
      key: "quadRoom",
      imgSrc: "/assets/img/yurt-fotograf/3-kisilik-on-cephe-106/14.jpg",
      link: "/service/quad-room",
    },
    {
      id: 9,
      key: "sixRoom",
      imgSrc: "/assets/img/yurt-fotograf/3-kisilik-on-cephe-106/17.jpg",
      link: "/service/six-room",
    },
    {
      id: 10,
      key: "exterior",
      imgSrc: "/assets/img/yurt-fotograf/dis-cephe/33.jpg",
      link: "/gallery",
    },
  ];

  useEffect(() => {
    if (!swiperRef.current) return;

    const swiperInstance = swiperRef.current.swiper;

    // ✅ Start autoplay properly
    if (swiperInstance && swiperInstance.autoplay) {
      swiperInstance.autoplay.start();
    }
    // ✅ Custom pagination with numbers
    if (swiperInstance.pagination) {
      swiperInstance.pagination.renderBullet = function (index, className) {
        let formattedNumber = index + 1 < 10 ? "0" + (index + 1) : index + 1;
        return `<span class="${className} number">${formattedNumber}</span>`;
      };
      swiperInstance.pagination.init();
      swiperInstance.pagination.update();
    }
    // ✅ Custom wheel effect for category slider
    const multiplier = {
      translate: 0.1,
      rotate: 0.01,
    };

    const calculateWheel = () => {
      const slides = document.querySelectorAll(".single");
      slides.forEach((slide) => {
        const rect = slide.getBoundingClientRect();
        const r = window.innerWidth * 0.5 - (rect.x + rect.width * 0.5);
        let ty =
          Math.abs(r) * multiplier.translate -
          rect.width * multiplier.translate;

        if (ty < 0) {
          ty = 0;
        }
        const transformOrigin = r < 0 ? "left top" : "right top";
        slide.style.transform = `translate(0, ${ty}px) rotate(${
          -r * multiplier.rotate
        }deg)`;
        slide.style.transformOrigin = transformOrigin;
      });
    };

    const raf = () => {
      requestAnimationFrame(raf);
      calculateWheel();
    };

    raf();

    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <section
      className="category-area bg-top-center category-area-with-bg"
      style={{
        backgroundImage: "url(/assets/img/yurt-fotograf/dis-cephe/33.jpg)",
        backgroundRepeat: "no-repeat",
        backgroundSize: "cover",
        backgroundPosition: "center",
        position: "relative",
      }}
      role="img"
      aria-label="Haseki Sultan Kız Öğrenci Yurdu - Yurt Kategorilerimiz Arka Plan"
      title="Haseki Sultan Kız Öğrenci Yurdu - Yurt Kategorilerimiz"
    >
      <div className="category-area-overlay"></div>
      <div
        className="container th-container"
        style={{ position: "relative", zIndex: 2 }}
      >
        <div className="title-area text-center">
          <span className="sub-title">{t("home.tourCategories.subTitle")}</span>
          <h2 className="sec-title">{t("home.tourCategories.title")}</h2>
        </div>

        <Swiper
          ref={swiperRef}
          modules={[Pagination, Autoplay]}
          slidesPerView={1}
          breakpoints={{
            576: { slidesPerView: 1 },
            768: { slidesPerView: 2 },
            992: { slidesPerView: 3 },
            1200: { slidesPerView: 3 },
            1400: { slidesPerView: 5 },
          }}
          spaceBetween={40}
          loop={true}
          autoplay={{ delay: 3000, disableOnInteraction: false }}
          speed={1000}
          pagination={{
            clickable: true,
            el: ".swiper-pagination",
            type: "bullets",
          }} // ✅ Defined renderBullet inside pagination
          className="th-slider has-shadow categorySlider"
        >
          {categories.map((category) => (
            <SwiperSlide key={category.id}>
              <div className="category-card single">
                <div className="box-img global-img">
                  <img
                    src={category.imgSrc}
                    alt={`Haseki Sultan Kız Öğrenci Yurdu - ${t(
                      `service.${category.key}.title`
                    )}`}
                    title={`Haseki Sultan Kız Öğrenci Yurdu - ${t(
                      `service.${category.key}.title`
                    )}`}
                    loading="lazy"
                  />
                </div>
                <h3 className="box-title">
                  <Link to={getLocalizedPath(category.link)}>
                    {t(`service.${category.key}.title`)}
                  </Link>
                </h3>
                <Link className="line-btn" to={getLocalizedPath(category.link)}>
                  {t("common.seeMore")}
                </Link>
              </div>
            </SwiperSlide>
          ))}
          <div className="slider-controller w-100 justify-content-center">
            <div
              className="swiper-pagination"
              style={{ maxWidth: "100%" }}
            ></div>
          </div>
        </Swiper>
      </div>
    </section>
  );
};

export default CategoryOne;
